import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import './Gate.css';

const PIN_LEN = 4;
const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];

const LockIcon = ({ green, blink }) => (
  <div className={`gate-lock-icon${green ? ' green' : ''}${blink ? ' blink' : ''}`}>
    <svg width="34" height="34" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id="gateLockGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3b82f6" />
          <stop offset="0.5" stopColor="#a855f7" />
          <stop offset="1" stopColor="#ec4899" />
        </linearGradient>
      </defs>
      <path
        fill="url(#gateLockGrad)"
        d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
      />
    </svg>
  </div>
);

const Gate = () => {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // lock | unlocking | rotating | login
  const [phase, setPhase] = useState('lock');
  const [entry, setEntry] = useState('');
  const [green, setGreen] = useState(false);
  const [blink, setBlink] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const entryRef = useRef('');
  const timers = useRef([]);
  const pushTimer = (t) => timers.current.push(t);

  useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), []);

  // Already authenticated → skip the gate.
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(user.role === 'va' ? '/va' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Countdown while locked.
  useEffect(() => {
    if (!locked) return undefined;
    const t = setInterval(() => {
      setWaitSeconds((s) => {
        if (s <= 1) {
          setLocked(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [locked]);

  const startUnlock = () => {
    setPhase('unlocking');
    setGreen(true);
    pushTimer(setTimeout(() => setBlink(true), 2000));
    pushTimer(setTimeout(() => {
      setBlink(false);
      setPhase('rotating');
    }, 3000));
    pushTimer(setTimeout(() => setPhase('login'), 4300));
  };

  const handleLocked = (seconds) => {
    entryRef.current = '';
    setEntry('');
    setError('');
    setLocked(true);
    setWaitSeconds(seconds > 0 ? seconds : 60);
  };

  const handleWrong = () => {
    setShaking(true);
    pushTimer(setTimeout(() => setShaking(false), 500));
    setError('Wrong code');
  };

  const submitPin = async (pin) => {
    setSubmitting(true);
    setError('');
    try {
      const response = await api.post('/auth/verify-pin', { pin });
      if (response.data && response.data.success) {
        startUnlock();
        return;
      }
      handleWrong();
    } catch (err) {
      const data = err.response?.data;
      if (data && data.locked) {
        handleLocked(data.waitSeconds);
      } else {
        handleWrong();
      }
    } finally {
      setSubmitting(false);
      if (phase === 'lock') {
        entryRef.current = '';
        setEntry('');
      }
    }
  };

  const press = (k) => {
    if (phase !== 'lock' || locked || submitting) return;
    if (k === 'back') {
      entryRef.current = entryRef.current.slice(0, -1);
      setEntry(entryRef.current);
      setError('');
      return;
    }
    if (k === 'clear') {
      entryRef.current = '';
      setEntry('');
      setError('');
      return;
    }
    if (entryRef.current.length >= PIN_LEN) return;
    entryRef.current += k;
    setEntry(entryRef.current);
    if (entryRef.current.length === PIN_LEN) {
      submitPin(entryRef.current);
    }
  };

  // PIN entry is click-only — no keyboard input on the keypad.

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const result = await login(loginForm.username, loginForm.password);
      if (result.success) {
        const role = result.user?.role;
        window.location.assign(`${window.location.origin}${role === 'va' ? '/va' : '/dashboard'}`);
      } else {
        setLoginError(result.error || 'Invalid username or password');
      }
    } catch (err) {
      setLoginError('Invalid username or password');
    } finally {
      setLoginLoading(false);
    }
  };

  const rotated = phase === 'rotating' || phase === 'login';
  const dots = Array.from({ length: PIN_LEN });

  return (
    <div className="gate-scene">
      <div className={`gate-cube${rotated ? ' rotated' : ''}`}>
        <div className="gface gside gside-left" />
        <div className="gface gside gside-right" />

        {/* Front face: PIN lock */}
        <div className="gface gfront">
          <div className={`gate-lock${shaking ? ' shake' : ''}`}>
            <LockIcon green={green} blink={blink} />
            <div className="gate-lock-title">{locked ? 'Locked' : 'Enter 4-digit code'}</div>

            {locked ? (
              <div className="gate-countdown">
                {Math.floor(waitSeconds / 60)}:{String(waitSeconds % 60).padStart(2, '0')}
                <span className="lbl">until you can try again</span>
              </div>
            ) : (
              <>
                <div className="gate-dots">
                  {dots.map((_, i) => (
                    <span key={i} className={i < entry.length ? 'filled' : ''} />
                  ))}
                </div>
                <div className="gate-pad">
                  {KEYPAD.map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={k === 'clear' || k === 'back' ? 'op' : ''}
                      onClick={() => press(k)}
                      disabled={submitting || phase !== 'lock'}
                    >
                      {k === 'clear' ? 'C' : k === 'back' ? '⌫' : k}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="gate-error">{error}</div>
          </div>
        </div>

        {/* Back face: username / password login */}
        <div className="gface gback">
          <form className="gate-login" onSubmit={handleLogin}>
            <h2>Sign In</h2>
            <p className="sub">Access your dashboard</p>

            <div>
              <label htmlFor="gate-username">Username</label>
              <input
                id="gate-username"
                type="text"
                autoComplete="username"
                autoFocus={phase === 'login'}
                placeholder="Enter your username"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="gate-password">Password</label>
              <input
                id="gate-password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              />
            </div>

            {loginError && <div className="gate-login-error">{loginError}</div>}

            <button type="submit" disabled={loginLoading}>
              {loginLoading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Gate;
